/**
 * Typed Firestore path builders.
 *
 * Every `doc(db, ...)` and `collection(db, ...)` pattern that appears
 * more than once in the frontend has a corresponding builder here.
 * Use these instead of inline strings so a path-rename or schema
 * change touches one file.
 *
 * The doc-id parameters that map to a known closed set (data
 * management subcollection, requirements subcollection) are typed as
 * unions so callers get autocomplete and tsc catches typos.
 */

import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
} from "firebase/firestore";
import { db } from "../firebase";
import { CROSS_FORM_CONFIRMATIONS_DOC } from '../features/crf/crossFormConfirmations';

// ── Top-level project ────────────────────────────────────────────────

export function projectsCollection(): CollectionReference {
  return collection(db, "projects");
}

export function projectDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId);
}

// ── Membership & invitations ────────────────────────────────────────

export function membersCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "members");
}

export function memberDoc(projectId: string, userId: string): DocumentReference {
  return doc(db, "projects", projectId, "members", userId);
}

export function invitationsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "invitations");
}

export function invitationDoc(projectId: string, token: string): DocumentReference {
  return doc(db, "projects", projectId, "invitations", token);
}

// ── Single-doc subcollections (one well-known doc per project) ──────

export function protocolAnalysisLatestDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "protocolAnalysis", "latest");
}

/**
 * Every protocol version this study has ever held — current and superseded.
 *
 * Read-only from a client, and that is a rule rather than a convention:
 * `firestore.rules` grants members `read` here and refuses create, update and
 * delete to EVERYONE, because `functions/src/protocol/protocolVersions.ts` is
 * the only writer and its guarantees (exactly one `current`, archive-before-
 * replace in one transaction, nothing ever deleted) are properties of that one
 * code path. Same posture as `essentialDocAttachmentsCollection`.
 */
export function protocolVersionsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "protocolVersions");
}

/**
 * A saved protocol comparison, reopenable without paying for the model call
 * again. Written only by `protocol/protocolComparisons.ts`; members read.
 */
/**
 * Every comparison this study has run, newest first when ordered by `ranAt`.
 *
 * Read-only from a client — `firestore.rules` grants members `read` and refuses
 * every write, because `protocol/protocolComparisons.ts` is the only writer.
 * Added 2026-08-26: the panel promised a saved comparison "can be reopened
 * without running it again" and nothing ever read one back, so a reload threw
 * the result away and charged the user another model call to recompute it.
 */
export function protocolComparisonsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "protocolComparisons");
}

export function protocolComparisonDoc(
  projectId: string,
  comparisonId: string,
): DocumentReference {
  return doc(db, "projects", projectId, "protocolComparisons", comparisonId);
}

export function studyDesignConfigDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "studyDesign", "config");
}

/**
 * Who confirmed the Schedule of Events, when, and a fingerprint of exactly
 * what they confirmed (coverage map G9). Written by `confirmScheduleV2`; the
 * doc id must stay in step with `SCHEDULE_CONFIRMATION_DOC` in
 * `functions/src/schedule/scheduleConfirmationStore.ts`.
 */
export function scheduleConfirmationDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "studyDesign", "scheduleConfirmation");
}

export function crfBuilderSessionDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "crfBuilder", "session");
}

export function crfPacketLatestDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "crfPacket", "latest");
}

export function crfPackageVisitMappingDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "crfPackage", "visitMapping");
}

/**
 * Automatic Project Validation — current run summary the dashboard subscribes
 * to (`projects/{id}/validation/latest`). Written server-side by the validation
 * orchestrator; read-only for project members. Immutable run history lives in
 * {@link validationRunsCollection}.
 */
export function validationLatestDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "validation", "latest");
}

export function validationRunsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "validationRuns");
}

/**
 * Guide-quality validation summary (`projects/{id}/validation/guide`). Written
 * server-side by the guide-quality run (Wave 3); read-only for members. Separate
 * from `latest` because the guide check runs on its own (expensive) trigger.
 */
export function validationGuideDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "validation", "guide");
}

// ── Artifacts subcollection ─────────────────────────────────────────

export function artifactsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "artifacts");
}

export function artifactDoc(projectId: string, artifactId: string): DocumentReference {
  return doc(db, "projects", projectId, "artifacts", artifactId);
}

export function artifactVersionsCollection(
  projectId: string,
  artifactId: string,
): CollectionReference {
  return collection(db, "projects", projectId, "artifacts", artifactId, "versions");
}

// ── Approvals subcollection (governance review records) ─────────────

export function approvalsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "approvals");
}

// ── Conversations / chat ────────────────────────────────────────────

export function conversationsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "conversations");
}

export function conversationMessagesCollection(
  projectId: string,
  conversationId: string,
): CollectionReference {
  return collection(
    db,
    "projects",
    projectId,
    "conversations",
    conversationId,
    "messages",
  );
}

// ── TMF / essential docs ────────────────────────────────────────────

export function essentialDocsCollection(projectId: string): CollectionReference {
  return collection(db, "projects", projectId, "essentialDocs");
}

/**
 * The files a TMF checklist entry holds — current and superseded alike.
 *
 * Read-only from a client, and that is a rule rather than a convention:
 * `firestore.rules` grants members `read` here and refuses create, update and
 * delete to EVERYONE, because `functions/src/tmf/attachmentStore.ts` is the
 * only writer and its guarantees (the scope guard, exactly one `current`,
 * nothing ever deleted) are properties of that one code path.
 */
export function essentialDocAttachmentsCollection(
  projectId: string,
  documentId: string,
): CollectionReference {
  return collection(
    db,
    "projects",
    projectId,
    "essentialDocs",
    documentId,
    "attachments",
  );
}

// ── Data Management subcollection ───────────────────────────────────

/**
 * Closed set of well-known docs under `projects/{id}/dataManagement/`.
 * Mirrors the subcollection layout enforced by Firestore rules.
 */
export type DataManagementDocId =
  | "plan"
  | "dmp"
  | "drcp"
  | "saerp"
  | "dblc"
  | "editCheckSpec"
  | "dataDictionary"
  | "sdtmMapping"
  | "edts"
  | "uatProtocol"
  | "uatReport"
  | "medicalCodingSpec"
  | "crfInstructionGuide"
  | "dictionaryAdoption";

export function dataManagementDoc(
  projectId: string,
  docId: DataManagementDocId,
): DocumentReference {
  return doc(db, "projects", projectId, "dataManagement", docId);
}

/**
 * Closed set of subcollection names that appear under
 * `projects/{id}/dataManagement/{docId}/`. Different artifact types use
 * different subcollections (DMP has `sections`, EditCheckSpec has
 * `checks`, SDTM mapping has all of `sections`/`variables`/`domains`,
 * UAT protocol has `sections`/`testCases`, CRF instruction guide has
 * `chunks`).
 */
export type DataManagementSubcollectionName =
  | "sections"
  | "checks"
  | "variables"
  | "domains"
  | "testCases"
  | "chunks";

export function dataManagementSubcollection(
  projectId: string,
  docId: DataManagementDocId,
  sub: DataManagementSubcollectionName,
): CollectionReference {
  return collection(db, "projects", projectId, "dataManagement", docId, sub);
}

export function crfInstructionGuideChunksCollection(
  projectId: string,
): CollectionReference {
  return dataManagementSubcollection(projectId, "crfInstructionGuide", "chunks");
}

// ── CRF Instruction Guide background job ─────────────────────────────

/**
 * Pointer doc the CRF-guide background job updates so the client can find
 * the active job: `projects/{id}/dataManagement/crfGuideJob` =
 * `{ jobId, updatedAt }`. Subscribe with onSnapshot; when a `jobId` is
 * present, subscribe to {@link guideJobDoc} for live status/progress.
 * (Not part of the `DataManagementDocId` closed set — it carries job
 * pointers, not a generated artifact.)
 */
export function crfGuideJobPointerDoc(projectId: string): DocumentReference {
  return doc(db, "projects", projectId, "dataManagement", "crfGuideJob");
}

/**
 * The per-job status/progress doc the CRF-guide background job writes to:
 * `projects/{id}/guideJobs/{jobId}`. Carries `status`, `formsTotal`,
 * `formsDone`, `formsFailed`, `formCodes`, `errorRef`, `errorMessage`,
 * `updatedAt`, etc. Readable by project members; written server-side.
 */
export function guideJobDoc(
  projectId: string,
  jobId: string,
): DocumentReference {
  return doc(db, "projects", projectId, "guideJobs", jobId);
}

// ── Requirements subcollection ──────────────────────────────────────

/**
 * Closed set of well-known docs under `projects/{id}/requirements/`.
 * Each maps to one of the Requirements & Design specifications.
 */
export type RequirementsDocId =
  | "urs"
  | "frs"
  | "sds"
  | "ads"
  | "atds"
  | "sacs"
  | "iis"
  | "testProcedure"
  | "testList"
  | "testPlan";

export function requirementsDoc(
  projectId: string,
  docId: RequirementsDocId,
): DocumentReference {
  return doc(db, "projects", projectId, "requirements", docId);
}

/**
 * Closed set of subcollection names that appear under
 * `projects/{id}/requirements/{docId}/`. URS / FRS use `requirements`;
 * SDS / ADS / ATDS / SACS use `designElements`; IIS uses the
 * IIS-specific `interfaceDesignElements`; all use `sections`.
 */
export type RequirementsSubcollectionName =
  | "sections"
  | "requirements"
  | "designElements"
  | "interfaceDesignElements";

export function requirementsSubcollection(
  projectId: string,
  docId: RequirementsDocId,
  sub: RequirementsSubcollectionName,
): CollectionReference {
  return collection(db, "projects", projectId, "requirements", docId, sub);
}

// ── Issued versions ─────────────────────────────────────────────────

/**
 * Issued-version snapshots live under `{sourceDocPath}/versions/`.
 * The parent path varies by doc family — Artifacts mount under
 * `projects/{p}/artifacts/{a}`, Data Management docs under
 * `projects/{p}/dataManagement/{docId}`, Requirements & Design docs
 * under `projects/{p}/requirements/{docId}`. The shape of each version
 * document (see IssuedVersion type) is parent-agnostic.
 *
 * `artifactVersionsCollection` (defined above) covers the Artifact
 * case; the helpers below cover the other two doc families.
 */

export function dataManagementVersionsCollection(
  projectId: string,
  docId: DataManagementDocId,
): CollectionReference {
  return collection(
    db,
    "projects",
    projectId,
    "dataManagement",
    docId,
    "versions",
  );
}

export function requirementsVersionsCollection(
  projectId: string,
  docId: RequirementsDocId,
): CollectionReference {
  return collection(
    db,
    "projects",
    projectId,
    "requirements",
    docId,
    "versions",
  );
}

/**
 * The study's cross-form duplicate confirmations: one field per confirmed
 * variable name, written with a merge so one confirmation cannot wipe another.
 * Kept out of crfBuilder/session because that document is rewritten whole on
 * every page change, which used to erase them (features/crf/crossFormConfirmations.ts).
 */
export function crfBuilderCrossFormConfirmationsDoc(projectId: string): DocumentReference {
  return doc(db, 'projects', projectId, 'crfBuilder', CROSS_FORM_CONFIRMATIONS_DOC);
}
